// The Clients board's single payload — GET /api/clients/board.
//
// One call, one arrangement. Every string here is a column value, a count over columns, or a
// template the server composed (src/server/routes/clientsBoard.ts): the page ARRANGES, it never
// derives. The types mirror BoardPayload exactly — when the server shape changes, this file
// changes with it and `bunx tsc --noEmit` says so.
//
// The board NEVER calls /brief. Browsing the portfolio must not burn the "new since you last
// looked" marker the briefs depend on, so `new_items` here is read from `client_visits`, never
// written back.
import { request } from './client.js';
import type { RailCell } from '../../shared/rail.js';
import type { Problem } from '../../shared/problems.js';

/** Routes are CONSTANT per cell and per rule — only anchors carry refs. */
export type BoardRoute =
  | { kind: 'in-page'; anchor: string }
  | { kind: 'screen'; screen: string; engagement_id?: number; anchor?: string | null }
  | { kind: 'related'; related: Record<string, unknown> }
  | null;

export type BoardRailKey = 'ENGAGED' | 'QUEUED' | 'BALL' | 'QUIET' | 'NEW';

/** `RailCell` from src/shared/rail.ts with this rail's key union applied — not a second shape. */
export interface BoardRailCell extends RailCell {
  key: BoardRailKey;
  route: BoardRoute;
}

/** The five keys, in the order the rail renders them, for the degraded and loading states. */
export const BOARD_RAIL_KEYS: readonly BoardRailKey[] = ['ENGAGED', 'QUEUED', 'BALL', 'QUIET', 'NEW'];

/** A move, exactly as `buildQueue` emitted it. Never re-worded on this side. */
export interface BoardQueueItem {
  rule: string;
  weight: number;
  title: string;
  why: string;
  chips: string[];
  route: BoardRoute;
  engagement: { id: number; code: string } | null;
  derived?: boolean;
}

export interface BoardEngagement {
  id: number;
  code: string;
  name: string;
  current_phase: string;
  phase_label: string;
  archived_at: string | null;
  gate: string | null;
  gate_title: string | null;
  auto_met: number | null;
  auto_total: number | null;
  gate_error: string | null;
  gate_skipped: string | null;
}

export interface BoardClient {
  id: number;
  name: string;
  legal_name: string | null;
  short_name: string | null;
  sector: string | null;
  city: string | null;
  country: string | null;
  status: 'active' | 'dormant' | 'archived';
  created_at: string;
  engagements: BoardEngagement[];
  archived_engagements: { id: number; code: string }[];
  /** `all-clear` already excluded server-side (B6): `queue.length` IS the move count. */
  queue: BoardQueueItem[];
  quiet_sentence: string | null;
  window: {
    first_contact: string | null;
    last_contact: string | null;
    days_in: number | null;
    days_since: number | null;
  };
  last_ref: string | null;
  ball: { ref: string; title: string; owner: 'us' | 'them' | null; open_days: number } | null;
  new_items: number;
  new_since: string;
  visited: boolean;
  counts: { documents: number; contacts: number; facts: number };
  zone: 'move' | 'quiet' | 'dark';
  quiet: boolean;
}

export interface BoardPayload {
  today: string;
  quiet_days: number;
  first_visit_window_days: number;
  on_file: number;
  engaged: number;
  scale_notice: string | null;
  readout: BoardRailCell[];
  clients: BoardClient[];
  problems: Problem[];
  generated_at: string;
}

export const boardApi = {
  /** The whole register, rolled up. No filter params — the filter is page-local (B4). */
  get: () => request<BoardPayload>('/clients/board'),
};

/** 'YYYY-MM-DD' → 'DD.MM.YYYY'. The board never lets the browser's clock format a server date. */
export const fmtBoardDate = (iso: string | null): string =>
  iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—';
